// AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { acquireToken, getAccount, initializeMsal, login, logout } from "./authConfig";

// Define types for the context
interface AuthContextProps {
    msalInstance: IPublicClientApplication | null;
    account: AccountInfo | null;
    isInitialized: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    acquireToken: () => Promise<string>;
}

// Create the context with an undefined initial value
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | null>(null);
    const [account, setAccount] = useState<AccountInfo | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        async function initAuth() {
            const instance = await initializeMsal();
            setMsalInstance(instance);
            setAccount(getAccount());
            setIsInitialized(true); // Mark as initialized once MSAL is set up
        }

        initAuth().catch(error => {
            console.error("Failed to initialize MSAL", error);
            setIsInitialized(true); // Still mark as initialized to prevent infinite loading
        });
    }, []);

    return (
        <AuthContext.Provider
            value={{
                msalInstance,
                account,
                isInitialized,
                login: async () => {
                    await login();
                    setAccount(getAccount());
                },
                logout: async () => {
                    await logout();
                    setAccount(null);
                },
                acquireToken
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Hook to access the Auth context in other components
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
